import type { GeneratorComparisonSummary } from "encamp-shared/src/facility/generatorComparisonSummary";

import {
  stateWasteGeneratorStatusToLabel,
  wasteGeneratorStatusToFullLabel,
} from "encamp-shared/src/waste";

import { LabeledValue } from "./LabeledValue";

export type ComparisonForDisplay = Pick<GeneratorComparisonSummary, "axis" | "direction"> &
  Partial<Omit<GeneratorComparisonSummary, "axis" | "direction">>;

export function priorityAxisLabel(comparison: ComparisonForDisplay): string {
  return comparison.axis === "STATE" ? "State" : "Federal";
}

export function priorityCalculatedLabel(comparison: ComparisonForDisplay): string {
  return comparison.calculatedStatus
    ? wasteGeneratorStatusToFullLabel(comparison.calculatedStatus)
    : "None calculated";
}

function shortStatusLabel(status: string | null | undefined): string {
  return status ? stateWasteGeneratorStatusToLabel(status) : "none";
}

/**
 * One mismatch-months field per comparison, in priority order, then a single consecutive-months
 * field. An axis with several tier pairs names each calculated tier, and the reported tier too
 * when two pairs share a calculated tier.
 */
export function ComparisonMismatchFields({
  comparisons,
}: {
  comparisons: readonly ComparisonForDisplay[];
}) {
  const sharedBy = (key: (comparison: ComparisonForDisplay) => string) => {
    return (comparison: ComparisonForDisplay) =>
      comparisons.filter((other) => key(other) === key(comparison)).length > 1;
  };
  const sharesAxis = sharedBy((comparison) => comparison.axis);
  const sharesTier = sharedBy(
    (comparison) => `${comparison.axis}|${comparison.calculatedStatus ?? ""}`
  );
  const tierName = (comparison: ComparisonForDisplay): string => {
    if (!sharesAxis(comparison)) return "";
    const calculated = shortStatusLabel(comparison.calculatedStatus);
    if (!sharesTier(comparison)) return ` ${calculated}`;
    return ` ${shortStatusLabel(comparison.reportedStatus)} → ${calculated}`;
  };
  const named = comparisons.map((comparison) => ({
    comparison,
    name: `${priorityAxisLabel(comparison)}${tierName(comparison)}`,
  }));
  const streak = (months: number | null | undefined) =>
    months == null ? "Unavailable" : String(months);
  return (
    <>
      {named.map(({ comparison, name }) => (
        <LabeledValue
          key={`${comparison.axis}|${comparison.reportedStatus}|${comparison.calculatedStatus}`}
          label={`${name} mismatch months`}
          value={
            comparison.mismatchMonths == null || comparison.spanMonths == null
              ? "Unavailable"
              : `${comparison.mismatchMonths} of ${comparison.spanMonths}`
          }
        />
      ))}
      {named.length > 0 && (
        <LabeledValue
          label="Consecutive months"
          value={
            named.length === 1
              ? streak(named[0]?.comparison.sustainedMonths)
              : named
                  .map(({ comparison, name }) => `${name} ${streak(comparison.sustainedMonths)}`)
                  .join(" · ")
          }
        />
      )}
    </>
  );
}
